import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageSquare, Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SmsSettingsPanel = ({ token }) => {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/sms/status`, { headers: { 'X-Admin-Password': token } })
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ configured: false }));
  }, [token]);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/sms/test`, { to }, { headers: { 'X-Admin-Password': token } });
      toast({ title: 'Test SMS sent ✓', description: `Delivered to ${data.to}` });
    } catch (err) {
      toast({ title: 'SMS failed', description: err?.response?.data?.detail || '' });
    } finally { setSending(false); }
  };

  const live = !!status?.configured;
  return (
    <div className="bg-white rounded-2xl border border-[#E5EAF2] p-6" data-testid="sms-settings-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h4 className="font-extrabold text-[#0A1628] flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#D4A24C]" /> SMS alerts · Twilio</h4>
          <p className="text-xs text-[#4A5A72] mt-1">Team alerts on new quotes, contacts & applications + customer quote confirmations.</p>
        </div>
        <span data-testid="sms-status-badge" className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${live ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
          {status === null ? '…' : live ? 'LIVE' : 'NOT CONFIGURED'}
        </span>
      </div>
      {status && (
        <div className="text-xs text-[#4A5A72] space-y-1 mb-4">
          <div>From number: <strong className="text-[#0A1628]" data-testid="sms-from-number">{status.fromNumber || '—'}</strong></div>
          <div>Team recipients: <strong className="text-[#0A1628]" data-testid="sms-recipients">{(status.alertRecipients || []).join(', ') || '—'}</strong></div>
          {!live && <div className="text-amber-700">Set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN and TWILIO_FROM_NUMBER in backend/.env to enable.</div>}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <Label className="text-xs text-[#4A5A72]">Send a test SMS to</Label>
          <Input type="tel" placeholder="+1 718 555 0100" value={to} onChange={e => setTo(e.target.value)} data-testid="sms-test-to-input" />
        </div>
        <Button onClick={sendTest} disabled={!live || sending || !to} data-testid="sms-test-send-btn" className="btn-accent gap-2">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
        </Button>
      </div>
    </div>
  );
};
